import {
  DangerSecondaryButton,
  Pagination,
  PrimaryButton,
  StatusTag,
} from "@graffer-inc/console-design-kit";
import React, { useState } from "react";

import type {
  Paging,
  ProcedureTag,
} from "../../../../application/domain/proc-management";
import { useAuthContextV2 } from "../../../../auth-state";
import { useModal } from "../../../../utils/custom-hooks";
import ProcTagDeleteModal from "./procedure-tag-delete-modal";
import { ProcTagUpdateModal } from "./procedure-tag-update-modal";

type Props = {
  procTags: ProcedureTag[];
  orgId: string;
  orgSlug: string;
  paging: Paging;
  onPageClick: (props: { orgId: string; page: number }) => void;
  onDelete: (tagId: string, pageNum: number) => void;
  onUpdate: (
    id: string,
    organizationId: string,
    name: string,
    priority: number,
    suppressOnPortal: boolean,
    pageNum: number,
  ) => void;
};

export const ProcTagsTable: React.FC<Props> = ({
  procTags,
  orgId,
  paging,
  onPageClick,
  onDelete,
  onUpdate,
}) => {
  const { auth } = useAuthContextV2();
  const updateModal = useModal();
  const [updateTarget, setUpdateTarget] = useState<ProcedureTag>();
  const [deleteTarget, setDeleteTarget] = useState<ProcedureTag | undefined>();
  const updateTag = (tag: ProcedureTag) => {
    if (auth.hasPermissions("proc_management.procedure_tags.update")) {
      updateModal.open();
      setUpdateTarget(tag);
    }
  };

  return (
    <>
      <div className="table-container">
        <table className="table is-fullwidth is-hoverable">
          <thead>
            <tr>
              <th>表示順</th>
              <th>トップページ掲載</th>
              <th>名称</th>
              <th>手続きタグID</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {procTags.map((tag) => (
              <tr key={tag.id} className="c-tr-has-tag">
                <td>{tag.priority}</td>
                <td>
                  {!tag.suppressOnPortal ? (
                    <StatusTag type={"SUCCESS"}>掲載する</StatusTag>
                  ) : (
                    <StatusTag type={"SECONDARY"}>掲載しない</StatusTag>
                  )}
                </td>
                <td className={"is-size-5"}>{tag.name}</td>
                <td>{tag.id}</td>
                <td>
                  <div className="buttons">
                    <PrimaryButton
                      text={"編集"}
                      onClick={() => updateTag(tag)}
                    />
                    {auth.hasPermissions(
                      "proc_management.procedure_tags.delete",
                    ) && (
                      <DangerSecondaryButton
                        text="削除"
                        onClick={() => setDeleteTarget(tag)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: "16px" }}>
          <Pagination
            onPageClick={(page) => {
              onPageClick({ orgId, page });
            }}
            currentPage={paging.current}
            lastPage={paging.last}
          />
        </div>
      </div>
      {deleteTarget && (
        <ProcTagDeleteModal
          deleteTarget={deleteTarget}
          onClose={() => setDeleteTarget(undefined)}
          onDelete={async (id: string, pageNum: number) => {
            await onDelete(id, pageNum);
            setDeleteTarget(undefined);
          }}
          pageNum={paging.current}
        />
      )}
      {updateTarget && (
        <ProcTagUpdateModal
          isActive={updateModal.isActive}
          onClose={updateModal.close}
          tag={updateTarget}
          onUpdate={onUpdate}
          pageNum={paging.current}
        />
      )}
    </>
  );
};

export default ProcTagsTable;
